import React, { useCallback, useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardMedia from '@mui/material/CardMedia'
import CircularProgress from '@mui/material/CircularProgress'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import useEmblaCarousel from 'embla-carousel-react'
import { EmblaCarouselType } from 'embla-carousel'
import { useCategoryContext } from 'context/CategoryContext'
import { useGetCategoryProducts } from 'hooks/useCategories'
import { getTranslations } from 'common/utils'
import { useNavigate } from 'react-router-dom'
import { colors } from 'styles/colors'
import TitleText from '../CustomText/TitleText'

type Props = {
  product: any
}

const PAGE_SIZE = 30
const LOAD_MORE_THRESHOLD = 0.92

const ProductCardSkeleton = () => {
  return (
    <Card
      sx={{
        width: 200,
        borderRadius: 3,
        boxShadow: 3
      }}
    >
      <Skeleton variant="rectangular" width={200} height={200} />
      <Stack p={1} gap={0.8}>
        <Skeleton variant="text" width="80%" height={24} />
        <Skeleton variant="text" width="55%" height={22} />
      </Stack>
    </Card>
  )
}

const ProductCard = React.memo(({ product }: Props) => {
  const navigate = useNavigate()
  const { ar, en } = getTranslations(product.translations)
  // A product can have no tag, and one missing tag used to take the whole page down.
  const { ar: tagAr, en: tagEn } = getTranslations(product.tag?.translations ?? [])

  return (
    <Card
      sx={{
        width: 200,
        borderRadius: 3,
        boxShadow: 3
      }}
    >
      <CardMedia
        component="img"
        image={product.avatar?.medium}
        alt={en?.name}
        sx={{
          width: 200,
          height: 200,
          objectFit: 'cover'
        }}
      />
      <Stack p={1} gap={0.8}>
        <TitleText
          text={en?.name ?? ''}
          title={ar?.name ?? ''}
          fontWeight={600}
          textAlign="left"
          fontSize={14}
          color={colors.black}
          lineHeight={1}
          noWrap
          onClick={() => navigate(`/products/all-products/${product.id}`)}
        />
        <TitleText
          text={tagEn?.name ?? ''}
          title={tagAr?.name ?? ''}
          fontSize={14}
          lineHeight={1}
          color={colors.black}
          noWrap
          onClick={() => navigate(`/products/all-products/${product.id}`)}
        />
      </Stack>
    </Card>
  )
})

const CustomCarousel = () => {
  const { category } = useCategoryContext()

  const [page, setPage] = useState(1)
  const [products, setProducts] = useState<any[]>([])
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)

  const loadingRef = useRef(false)

  const [emblaRef, emblaApi] = useEmblaCarousel({
    dragFree: true,
    loop: false,
    watchResize: false,
    watchSlides: true,
  })

  const { refetch } = useGetCategoryProducts(
    {
      page,
      size: PAGE_SIZE,
      categoryId: category.id
    },
    {
      select: (res: any) => res.products,
      onSuccess: (res: any) => {
        setProducts((prev) => {
          if (page === 1) return res || []

          const existing = new Set(prev.map((p) => p.id))
          const next = (res || []).filter((p: any) => !existing.has(p.id))
          return [...prev, ...next]
        })

        setHasMore(!!res && res.length === PAGE_SIZE)
        loadingRef.current = false
        setLoadingMore(false)
      },
      onError: () => {
        loadingRef.current = false
        setLoadingMore(false)
      }
    }
  )

  useEffect(() => {
    setPage(1)
    setProducts([])
    setHasMore(true)
    setLoadingMore(true)
    loadingRef.current = true
    refetch()
  }, [category.id, refetch])

  useEffect(() => {
    if (page === 1) return
    refetch()
  }, [page, refetch])

  const loadNextPage = useCallback(() => {
    if (loadingRef.current || loadingMore || !hasMore) return

    loadingRef.current = true
    setLoadingMore(true)
    setPage((prev) => prev + 1)
  }, [loadingMore, hasMore])

  const onScroll = useCallback(
    (api: EmblaCarouselType) => {
      if (loadingRef.current || loadingMore || !hasMore) return

      const progress = api.scrollProgress()

      if (progress >= LOAD_MORE_THRESHOLD) {
        loadNextPage()
      }
    },
    [loadingMore, hasMore, loadNextPage]
  )

  useEffect(() => {
    if (!emblaApi) return

    const handler = () => onScroll(emblaApi)

    emblaApi.on('scroll', handler)
    return () => {
      emblaApi.off('scroll', handler)
    }
  }, [emblaApi, onScroll])

  const isInitialLoading = loadingMore && products.length === 0

  return (
    <Box>
      <Box className="embla" sx={{ width: '100%' }}>
        <Box
          className="embla__viewport"
          ref={emblaRef}
          sx={{ overflow: 'hidden', px: 2 }}
        >
          <Box
            className="embla__container"
            sx={{ display: 'flex', gap: 2 }}
          >
            {isInitialLoading
              ? Array.from({ length: 10 }).map((_, index) => (
                <Box
                  key={`initial-skeleton-${index}`}
                  className="embla__slide"
                  sx={{
                    flex: '0 0 auto',
                    minWidth: 0,
                    marginTop: 2,
                    marginBottom: 2
                  }}
                >
                  <ProductCardSkeleton />
                </Box>
              ))
              : products.map((product: any, index: number) => (
                <Box
                  className="embla__slide"
                  key={product.id}
                  sx={{
                    flex: '0 0 auto',
                    minWidth: 0,
                    marginRight: index === products.length - 1 ? 2 : 0,
                    marginTop: 2,
                    marginBottom: 2
                  }}
                >
                  <ProductCard product={product} />
                </Box>
              ))}

            {!isInitialLoading && hasMore && (
              <Box
                className="embla__slide"
                sx={{
                  flex: '0 0 48px',
                  minWidth: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: 2,
                  marginBottom: 2,
                  marginRight: 2
                }}
              >
                {loadingMore ? <CircularProgress size={22} /> : null}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default CustomCarousel